import { useEffect, useMemo, useState } from "react";
import { listQcSoldLeads, updateQcChecklist } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { toast } from "sonner";
import { ShieldCheck, Search, CheckCircle2, Circle, Lock } from "lucide-react";

const CHECKLIST = [
  { key: "wa_welcome_tee_card", label: "Client welcomed on WA with Tee + Card" },
  { key: "billing_done", label: "Billing done within 24 hrs" },
  { key: "tshirt_billing_done_compliance", label: "T-shirt billing done" },
  { key: "added_to_trainerise", label: "Added to Trainerise" },
  { key: "trainerise_welcome_video", label: "Welcome video sent on Trainerise" },
  { key: "nutrition_consult_booked_24h", label: "Nutrition Consult booked (24 hrs)" },
  { key: "mid_week_consult_booked_24h", label: "Mid-week consult booked (24 hrs)" },
  { key: "final_week_consult_booked_24h", label: "Final-week consult booked (24 hrs)" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function QCChecks() {
  const { user } = useAuth();
  const canEdit = atLeast(user?.role, "admin") || !!user?.qc_access;
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | complete
  const [saving, setSaving] = useState({});

  const load = () => listQcSoldLeads().then(setRows).catch((e) => {
    if (e?.response?.status === 403) {
      setRows([]);
      toast.error("You do not have QC access. Ask an admin to enable it.");
    } else {
      toast.error(e?.response?.data?.detail || "Failed to load QC list");
    }
  });
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (needle) {
        const hay = `${r.name || ""} ${r.phone || ""} ${r.sales_ex || ""} ${r.acc_coach || ""} ${r.programme || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (filter === "pending" && r.qc_complete) return false;
      if (filter === "complete" && !r.qc_complete) return false;
      return true;
    });
  }, [rows, q, filter]);

  const toggle = async (row, key, next) => {
    if (!canEdit) return;
    setSaving((s) => ({ ...s, [`${row.id}:${key}`]: true }));
    // optimistic update
    setRows((rs) => (rs || []).map((r) => {
      if (r.id !== row.id) return r;
      const patched = { ...r, [key]: next };
      patched.qc_complete = CHECKLIST.every((c) => bool(patched[c.key]));
      return patched;
    }));
    try {
      const updated = await updateQcChecklist(row.id, { [key]: next });
      setRows((rs) => (rs || []).map((r) => {
        if (r.id !== row.id) return r;
        const patched = { ...r, ...Object.fromEntries(CHECKLIST.map((c) => [c.key, updated[c.key]])) };
        patched.qc_complete = CHECKLIST.every((c) => bool(patched[c.key]));
        return patched;
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      // roll back
      load();
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[`${row.id}:${key}`]; return c; });
    }
  };

  const pendingCount = (rows || []).filter((r) => !r.qc_complete).length;
  const completeCount = (rows || []).filter((r) => r.qc_complete).length;

  return (
    <div className="space-y-6" data-testid="qc-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            Post-sale compliance audit
          </div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">QC Checks</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Every Sold lead in this facility. Verify the post-sale checklist item by item — the row goes green once all boxes are ticked.
            {canEdit ? " You have edit access." : " View-only for you — ask an admin to grant QC access."}
          </p>
        </div>
        {!canEdit && (
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-500/30 bg-zinc-500/10 px-3 py-1 text-xs text-zinc-300" data-testid="qc-readonly-badge">
            <Lock className="h-3 w-3" /> View-only
          </div>
        )}
      </div>

      {rows && rows.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="Total sold" value={rows.length} tone="zinc" />
          <Kpi label="Pending QC" value={pendingCount} tone={pendingCount > 0 ? "rose" : "zinc"} />
          <Kpi label="QC complete" value={completeCount} tone="emerald" />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, rep, coach, programme…"
            className="pl-8 pr-3 py-2 rounded-full border border-[#262626] bg-[#0a0a0a] text-sm text-zinc-100 placeholder:text-zinc-600 w-72 focus:outline-none focus:border-blue-500/60"
            data-testid="qc-search"
          />
        </div>
        <div className="inline-flex rounded-full border border-[#262626] bg-[#0a0a0a] p-0.5 text-xs" data-testid="qc-filter">
          {["all", "pending", "complete"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full transition-colors capitalize ${filter === f ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid={`qc-filter-${f}`}
            >{f}</button>
          ))}
        </div>
      </div>

      <section className="card-surface p-0 overflow-x-auto" data-testid="qc-table">
        {rows === null ? (
          <div className="text-sm text-zinc-500 p-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-zinc-500 p-8 text-center">
            {rows.length === 0 ? "No Sold leads to audit yet." : "No rows match the current filter/search."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Programme</th>
                <th className="px-3 py-2 font-medium">Sold on</th>
                <th className="px-3 py-2 font-medium">Rep</th>
                <th className="px-3 py-2 font-medium">Acc. coach</th>
                {CHECKLIST.map((c) => (
                  <th key={c.key} className="px-2 py-2 font-medium text-center min-w-[140px]">{c.label}</th>
                ))}
                <th className="px-3 py-2 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {(filtered || []).map((r) => (
                <tr key={r.id} className={`border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] ${r.qc_complete ? "bg-emerald-500/5" : ""}`} data-testid={`qc-row-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="text-zinc-100 font-medium">{r.name || "—"}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{r.phone || ""}</div>
                    {r.recording_skipped_at && (
                      r.recording_skipped_reason === "declined" ? (
                        <div className="mt-1 inline-flex rounded-full border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-300"
                          title={`Member declined being recorded, noted by ${r.recording_skipped_by || "?"} on ${fmtDate(r.recording_skipped_at)}`}>
                          Member declined recording
                        </div>
                      ) : (
                      <div className="mt-1 inline-flex rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-300"
                        title={`Saved without a call recording by ${r.recording_skipped_by || "?"} on ${fmtDate(r.recording_skipped_at)}`}>
                        Saved without recording
                      </div>
                      )
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-300">{r.programme || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5 text-zinc-300">{r.sales_ex || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-300" data-testid={`qc-coach-${r.id}`}>{r.acc_coach || "—"}</td>
                  {CHECKLIST.map((c) => {
                    const on = bool(r[c.key]);
                    const busy = !!saving[`${r.id}:${c.key}`];
                    return (
                      <td key={c.key} className="px-2 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => toggle(r, c.key, !on)}
                          disabled={!canEdit || busy}
                          className={`p-1.5 rounded-full transition-colors ${on ? "text-emerald-300 hover:bg-emerald-500/10" : "text-zinc-600 hover:bg-zinc-500/10"} ${!canEdit ? "cursor-not-allowed opacity-70" : ""}`}
                          data-testid={`qc-toggle-${r.id}-${c.key}`}
                          aria-label={c.label}
                        >
                          {on ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-right">
                    {r.qc_complete
                      ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Complete</span>
                      : <span className="text-[11px] text-rose-300">Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

const bool = (v) => v === true || v === "true" || v === 1 || v === "1";

function Kpi({ label, value, tone }) {
  const tones = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-200",
    zinc: "border-[#262626] bg-[#0a0a0a] text-zinc-200",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold opacity-80">{label}</div>
      <div className="font-display text-3xl font-extrabold tabular-nums mt-1">{value}</div>
    </div>
  );
}
